import { motion } from "framer-motion";
import { History, UserPlus, UserMinus, RefreshCw, User } from "lucide-react";
import { FADE_IN, SLIDE_UP } from "../lib/animations";

const ActivityLog = ({ logs, onRefresh, loading }) => {
  const getActionIcon = (action) => {
    switch (action) {
      case "ADDED": return <UserPlus className="w-3.5 h-3.5 text-emerald-400" />;
      case "UPDATED": return <RefreshCw className="w-3.5 h-3.5 text-[#00E5FF]" />;
      case "DELETED": return <UserMinus className="w-3.5 h-3.5 text-[#FF3366]" />;
      case "LOGIN": return <User className="w-3.5 h-3.5 text-purple-400" />;
      default: return <History className="w-3.5 h-3.5 text-zinc-500" />;
    }
  };

  const getActionClass = (action) => {
    switch (action) {
      case "ADDED": return "bg-emerald-500/15 text-emerald-400 border-emerald-500/30";
      case "UPDATED": return "bg-[#00E5FF]/15 text-[#00E5FF] border-[#00E5FF]/30";
      case "DELETED": return "bg-[#FF3366]/15 text-[#FF3366] border-[#FF3366]/30";
      case "LOGIN": return "bg-purple-500/15 text-purple-400 border-purple-500/30";
      default: return "bg-zinc-500/15 text-zinc-400 border-zinc-500/30";
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    if (diff < 60000) return "Just now";
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  return (
    <motion.div
      {...SLIDE_UP}
      transition={{ delay: 0.2, duration: 0.25 }}
      className="surface-card overflow-hidden mt-6"
      data-testid="activity-log-panel"
    >
      <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 flex items-center justify-center border border-purple-500/30 bg-purple-500/10">
            <History className="w-3.5 h-3.5 text-purple-400" />
          </div>
          <div>
            <h3 className="font-mono text-xs font-bold text-zinc-200 uppercase tracking-wider">Activity Log</h3>
            <p className="font-mono text-[9px] text-zinc-600">Recent changes</p>
          </div>
        </div>
        <button
          onClick={onRefresh}
          className="p-1.5 hover:bg-zinc-800 transition-colors"
          data-testid="refresh-logs-btn"
        >
          <RefreshCw className={`w-3.5 h-3.5 text-zinc-500 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      <div className="max-h-[350px] overflow-y-auto scrollbar-thin">
        {logs.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-zinc-600 font-mono text-xs">NO ACTIVITY YET</p>
          </div>
        ) : (
          <div className="divide-y divide-zinc-800/50">
            {logs.map((log, index) => (
              <motion.div
                key={log.id}
                {...FADE_IN}
                transition={{ delay: index * 0.02 }}
                className="px-4 py-3 hover:bg-zinc-800/20 transition-colors"
                data-testid={`activity-log-${log.id}`}
              >
                <div className="flex items-start gap-2.5">
                  <div className={`w-7 h-7 flex items-center justify-center border shrink-0 ${getActionClass(log.action)}`}>
                    {getActionIcon(log.action)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className={`text-[9px] font-mono font-bold px-1.5 py-0.5 border ${getActionClass(log.action)}`}>
                        {log.action}
                      </span>
                      <span className="text-xs text-zinc-300 font-medium truncate">{log.staff_name}</span>
                    </div>
                    {log.details && (
                      <p className="text-[11px] text-zinc-500 truncate mb-0.5">{log.details}</p>
                    )}
                    <div className="flex items-center gap-1.5 text-[10px] text-zinc-600 font-mono">
                      <span>{log.performed_by}</span>
                      <span className="text-zinc-700">|</span>
                      <span>{formatTime(log.timestamp)}</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default ActivityLog;
